import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { useOverwriteCustomerHoverDebounce } from './customer-summary.hooks';
import { getCustomerSummaryForCustomerId } from './customer-summary.api';
import CustomerSummary from './customer-summary';
import ErrorPopover from './error-popover';
import UnavailablePopover from './unavailable-popover';

import style from './style.module.less';

const CustomerSummaryPopover = ({ recipientName, customerId = '', hideCustomerSummaryPopover }) => {
  const [data, setData] = useAsyncSafeState({});
  const [hasError, setHasError] = useAsyncSafeState(false);
  const [timer, setTimer] = useState(null);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const hoverDebounce = useOverwriteCustomerHoverDebounce() ?? 250;

  const fetchCustomerData = useCallback(async () => {
    try {
      const result = await getCustomerSummaryForCustomerId(customerId);
      setData(result);
    } catch {
      setHasError(true);
    }
  }, [setHasError, customerId, setData]);

  const debounceFetch = useCallback(() => {
    setHasError(false);
    clearTimeout(timer);
    if (showUnavailable) {
      return;
    }
    const id = setTimeout(() => {
      fetchCustomerData();
    }, hoverDebounce);
    setTimer(id);
  }, [fetchCustomerData, setTimer, timer, setHasError, hoverDebounce, showUnavailable]);

  useEffect(() => {
    setShowUnavailable(hoverDebounce < 0);
  }, [hoverDebounce]);

  if (customerId === '' || customerId === null || hideCustomerSummaryPopover) {
    return <div className={style['recipient-name']}>{recipientName}</div>;
  }

  const isLoadingData = !hasError && Object.keys(data).length === 0;
  const Content = () => {
    if (showUnavailable) {
      return <UnavailablePopover />;
    }

    if (hasError) {
      return <ErrorPopover />;
    }

    if (isLoadingData) {
      return (
        <div className={style.summary}>
          <LoadingIndicator className={style.loading} />
        </div>
      );
    }

    return <CustomerSummary recipientName={recipientName} {...data} />;
  };

  return (
    <Tooltip>
      <div
        className={classnames(style['recipient-name'], {
          [style['link-behavior']]: !!customerId,
        })}
        onMouseEnter={debounceFetch}
        onMouseLeave={() => clearTimeout(timer)}
      >
        {recipientName}
      </div>
      <TooltipContent>
        <Content />
      </TooltipContent>
    </Tooltip>
  );
};

CustomerSummaryPopover.propTypes = {
  hideCustomerSummaryPopover: PropTypes.bool.isRequired,
  recipientName: PropTypes.string.isRequired,
  customerId: PropTypes.string,
};

export default CustomerSummaryPopover;
